import {
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  heliosDefaultToolPanels,
} from '@helios/ag-grid-config'
import { SavedView } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState, InlineEditDataType } from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { TransactionGridKey } from '../../../panel-key'
import { Transaction } from '../../models/transaction.model'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'

const transactionsPath = composeFieldPath([] as Transaction[])

@Component({
  selector: 'hs-transactions-grid[data]',
  templateUrl: './transactions-grid.component.html',
})
export class TransactionsGridComponent implements OnInit, OnDestroy {
  @Input() data: Transaction[] | null = null
  @Input() savedViews: SavedView[] | null | undefined = null

  private readonly destroySubject$ = new Subject<void>()

  clearFiltersOnAccountLoadToggle = new FormControl(true)

  /** @internal */
  gridApi?: AgGridApi

  gridOptions: GridOptions = {
    onRowDataChanged: event => this.onRowDataChanged(event),
    columnDefs: [
      {
        field: transactionsPath('id'),
        headerName: 'Ext Transaction ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: transactionsPath('executionId'),
        headerName: 'Execution ID',
        type: NfColumnTypes.Identifier,
      },
      { field: transactionsPath('reconDate'), type: NfColumnTypes.Date },
      { field: transactionsPath('dataSourceType') },
      { field: transactionsPath('transactionType') },
      { field: transactionsPath('entryDate'), type: NfColumnTypes.Date },
      { field: transactionsPath('settleDate'), type: NfColumnTypes.Date },
      { field: transactionsPath('postDate'), type: NfColumnTypes.Date },
      { field: transactionsPath('units'), type: NfColumnTypes.Identifier },
      { field: transactionsPath('amount'), type: NfColumnTypes.Identifier },
      { field: transactionsPath('price'), type: NfColumnTypes.Identifier },
      {
        field: transactionsPath('commission'),
        type: NfColumnTypes.Identifier,
      },
      { field: transactionsPath('originationFee'), type: NfColumnTypes.Number },
      {
        field: transactionsPath('prepaidInterest'),
        type: NfColumnTypes.Number,
      },
      { field: transactionsPath('interestDue'), type: NfColumnTypes.Number },
      { field: transactionsPath('fee'), type: NfColumnTypes.Identifier },
      { field: transactionsPath('factor'), type: NfColumnTypes.Identifier },
      { field: transactionsPath('description') },
      { field: transactionsPath('executionType') },
      {
        field: transactionsPath('currencyId'),
        headerName: 'Currency ID',
        type: NfColumnTypes.Identifier,
      },
      { field: transactionsPath('alternateId'), headerName: 'Alternate ID' },
      {
        field: transactionsPath('alternateIdDesc'),
        headerName: 'Alternate ID Description',
      },
      { field: transactionsPath('counterPartyName') },
      { field: transactionsPath('aggInfoString') },
      {
        field: transactionsPath('prometheusTransactionId'),
        headerName: 'Prometheus Transaction ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: transactionsPath('prometheusLinkingId'),
        headerName: 'Prometheus Linking ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: transactionsPath('unscaledPrice'),
        type: NfColumnTypes.Identifier,
      },
      {
        field: transactionsPath('custodyTransactionIdentifier'),
        headerName: 'Custody Transaction ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: transactionsPath('originId'),
        headerName: 'Origin ID',
        type: NfColumnTypes.Identifier,
      },
      { field: transactionsPath('maturityDate'), type: NfColumnTypes.Date },
      {
        field: transactionsPath('dataSourceId'),
        headerName: 'Data Source ID',
        type: NfColumnTypes.Identifier,
      },
      { field: transactionsPath('cusip'), type: NfColumnTypes.Identifier },
      { field: transactionsPath('isin'), type: NfColumnTypes.Identifier },
      {
        field: transactionsPath('securityId'),
        type: NfColumnTypes.Identifier,
        headerName: 'Security ID',
      },
      {
        field: transactionsPath('isCapitalize'),
        type: InlineEditDataType.Checkbox,
      },
    ],
    icons: {
      panelSettings: generateMdiIcon('mdi-cog'),
    },
    sideBar: generateHeliosAgGridSidebarFromScratch([
      createPanelSettingsToolPanel({
        controls: [
          {
            label: 'Clear Filters on Load',
            formControl: this.clearFiltersOnAccountLoadToggle,
          },
        ],
      }),
      ...heliosDefaultToolPanels,
    ]),
    onGridReady: event => this.onGridReady(event),
    onModelUpdated: event => this.onModelUpdated(event),
  }

  private onModelUpdated(event: ModelUpdatedEvent): void {
    if (event.newData && !event.api.isColumnFilterPresent()) {
      // get state from store
      const filterState = this.savedViews?.find(
        view => view.gridKey === TransactionGridKey,
      )
      if (filterState) {
        // Apply preserved filters once grid is rendered
        event.api.setFilterModel(filterState.savedView.filterState)
      }
    }
  }

  private async onGridReady(event: GridReadyEvent) {
    this.gridApi = event
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    enableHeliosCopyBehavior(event.api)
    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace 
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      TransactionGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
  }

  constructor(
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnInit(): void {
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  private onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data != null) {
      return
    }
    event.api.showLoadingOverlay()
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }
}
